import { useCallback } from "react";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import type { Nurse, NurseInShift } from "../../utils/models";
import type { ShiftWindow } from "../../utils/scheduleConflict";
import { getShiftIndexByShiftTimes } from "../../utils/utilFunctions";
import { useStaffingTargetCheck } from "../../utils/warningDialogs/useStaffingTargetCheck";
import { type NurseWarningState, useNurseWarnings, type WarningDialogProps } from "../../utils/warningDialogs/WarningDialog";
import { quickAddPersonWarningInputs } from "./quickAddWarningInputs";

// The confirm/cancel handlers `useNurseWarnings` returns after its state and `checkWarnings`, in its order.
type Handlers = ReturnType<typeof useNurseWarnings> extends [unknown, unknown, ...infer Rest] ? Rest : never;

/** The cascade's state and handlers, laid out as `WarningDialog`'s props. */
const toWarningDialogProps = (state: NurseWarningState, handlers: Handlers, longShiftDuration: number | undefined): WarningDialogProps => {
	const [onConfirmAgency, onConfirmOvertime, onConfirmDailyHours, onCancel, onConfirmPto, onConfirmTarget, onConfirmEmployeeStatus] = handlers;
	return {
		openPtoWarning: state.isPtoWarningOpen,
		openTargetWarning: state.isTargetWarningOpen,
		openAgencyWarning: state.isAgencyWarningOpen,
		openOvertimeWarning: state.isOvertimeWarningOpen,
		openDailyHoursWarning: state.isDailyHoursWarningOpen,
		openEmployeeStatusWarning: state.isEmployeeStatusWarningOpen,
		overtimeDetails: state.overtimeDetails,
		dailyHoursDetails: state.dailyHoursDetails,
		targetDetails: state.targetDetails,
		employeeStatusDetails: state.employeeStatusDetails,
		longShiftDuration,
		...{
			onCancelPto: onCancel,
			onCancelTarget: onCancel,
			onCancelAgency: onCancel,
			onCancelOvertime: onCancel,
			onCancelDailyHours: onCancel,
			onCancelEmployeeStatus: onCancel,
		},
		...{ onConfirmPto, onConfirmTarget, onConfirmAgency, onConfirmOvertime, onConfirmDailyHours, onConfirmEmployeeStatus },
	};
};

type Params = {
	nurse: Nurse | null;
	location: string | null;
	window: ShiftWindow;
	movingShift: NurseInShift | undefined;
	weekIndex: number;
	dayIndex: number;
	showActual: boolean;
	onComplete: () => void;
};

/**
 * The "+" dialog's pre-save checks: the destination's staffing target always, then the person's own (see
 * `quickAddPersonWarningInputs`), and `onComplete` once every warning shown was confirmed.
 */
export const useQuickAddWarnings = ({ nurse, location, window, movingShift, weekIndex, dayIndex, showActual, onComplete }: Params) => {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const getTargetCheck = useStaffingTargetCheck();
	const [warningState, checkWarnings, ...handlers] = useNurseWarnings(onComplete, chosenDepartment.longShiftDuration);

	const handleSave = useCallback(() => {
		if (!nurse) throw new Error("useQuickAddWarnings: save reached with no person picked");
		const weeklyShifts = hospitalData.weeks[weekIndex]?.weeklyShifts ?? [];
		const person = quickAddPersonWarningInputs({ nurse, department: chosenDepartment, weeklyShifts, dayIndex, window, movingShift, jobManagementEnabled });
		const targetWarning = getTargetCheck({
			locationName: location,
			weekIndex,
			dayNo: dayIndex,
			shiftIndex: getShiftIndexByShiftTimes(window),
			isActual: showActual,
		});
		checkWarnings(person.isAgency, undefined, undefined, person.dailyHours, showActual, person.hasPto, targetWarning, person.employeeStatusWarning);
	}, [
		nurse,
		hospitalData.weeks,
		weekIndex,
		chosenDepartment,
		dayIndex,
		window,
		movingShift,
		jobManagementEnabled,
		getTargetCheck,
		location,
		showActual,
		checkWarnings,
	]);

	return { handleSave, warningDialogProps: toWarningDialogProps(warningState, handlers, chosenDepartment.longShiftDuration) };
};
